/**
 * @file database.service.ts
 * @version 2.3.0
 * @datetime 2026-09-06T02:35:00+03:00
 * @description PouchDB and CouchDB synchronization service with Gemini 3.1 AI integration, multi-format document handling, and dynamic user scoping.
 */

import { Injectable, inject, signal, computed } from '@angular/core';
import PouchDB from 'pouchdb-browser';
import PouchDBFind from 'pouchdb-find';
import { environment } from '../../environments/environment';
import { SocketService } from './socket.service';
import { GoogleWorkspaceService } from './google-workspace.service';

// Register plugins
PouchDB.plugin(PouchDBFind);

export interface CurrencyRateItem {
  _id?: string;
  _rev?: string;
  type: 'currency_rate';
  code: string;
  name: string;
  buying: number;
  selling: number;
  changeRate: number;
  updatedAt: string;
}

export interface CustomerItem {
  _id?: string;
  _rev?: string;
  vkn?: string;
  type: 'customer' | 'account';
  account?: string;
  accountCode?: string;
  subType?: 'customer' | 'seller';
  name: string;
  company?: string;
  phone?: string;
  email?: string;
  balance: number;
  status?: 'active' | 'passive';
  city?: string;
  vknTckn?: string;
  taxOffice?: string;
  address?: string;
  naceCodes?: Array<{ code: string; description?: string; isPrimary?: boolean }>;
  taxDeclarations?: Array<{ year: number | string; declaredMatrah: number | string; accruedTax: number | string }>;
  gibData?: any;
  sourceDoc?: string;
  createdAt?: string;
}

export interface ProductItem {
  _id?: string;
  _rev?: string;
  vkn?: string;
  type: 'product';
  name: string;
  code: string;
  category: string;
  price: number;
  stock: number;
  unit: string;
  createdAt?: string;
}

export interface InvoiceItem {
  _id?: string;
  _rev?: string;
  vkn?: string;
  type: 'invoice';
  invoiceNo: string;
  customerName: string;
  date: string;
  totalAmount: number;
  currency: string;
  status: 'paid' | 'pending' | 'draft';
  createdAt?: string;
}

export interface ChatMessageItem {
  _id?: string;
  _rev?: string;
  vkn?: string;
  type: 'chat_message';
  sender: 'user' | 'assistant' | 'system';
  text: string;
  attachments?: Array<{
    fileName: string;
    fileSizeFormatted?: string;
    fileType?: string;
    file?: File;
    dataUrl?: string;
  }>;
  structuredData?: any;
  tokenUsage?: {
    tokensIn: number;
    tokensOut: number;
    totalTokens: number;
    model?: string;
  };
  timestamp: string;
  createdAt?: string;
}

export interface HesapPlaniItem {
  _id: string;
  _rev?: string;
  id: string;
  balanceSheetAccount: string;
  balanceSheetAccountName: string;
  groupAccount: string;
  groupAccountName: string;
  mainAccount: string;
  mainAccountName: string;
  accountNameEn: string;
  accountName: string;
  accountCode: string;
  classification: string;
}

export interface YevmiyeTransactionItem {
  _id?: string;
  _rev?: string;
  id?: string;
  type?: 'yevmiye';
  voucherNumber: string;
  sequenceNumber?: number;
  orderNumber: string | number;
  entryNumber?: number;
  entryDate?: string;
  date: string;
  accountCode: string;
  accountName: string;
  debit: number;
  credit: number;
  description?: string;
  documentType?: string;
  documentNumber?: string;
  documentDate?: string;
  counterpartTaxId?: string;
  counterpartTitle?: string;
  paymentMethod?: string;
  registrationDelayDays?: number;
  isCashCeilingBreach?: boolean;
  esgScope?: string;
  rawAccountCode?: string;
  classification?: string;
  mainAccountCode?: string;
  mainAccountName?: string;
  currency?: string;
  currencyDebt?: number;
  currencyCredit?: number;
  currencyRate?: number;
  [key: string]: any;
}

export interface UploadedDocumentItem {
  _id?: string;
  _rev?: string;
  vkn: string;
  type: 'original_doc';
  fileName: string;
  fileSize: number;
  fileSizeFormatted: string;
  documentType: 'PDF' | 'EXCEL' | 'IMAGE' | 'CSV' | 'XLSX' | 'DOC' | 'XML' | 'JSON' | 'DİĞER';
  mimeType: string;
  uploadDateTime: string; // YYYY-MM-DD HH:mm:ss
  dataUrl?: string;
  status: 'uploaded' | 'processing' | 'processed';
}

export interface TokenUsageItem {
  _id?: string;
  _rev?: string;
  vkn: string;
  type: 'token_usage';
  date: string; // YYYY-MM-DD
  dateTime: string; // YYYY-MM-DD HH:mm:ss
  feature: 'Gemini Live' | 'Sesli Fatura' | 'Asistan Sohbet' | 'Evrak Analiz' | 'Google Sync';
  model: string;
  tokensIn: number;
  tokensOut: number;
  totalTokens: number;
}

export interface AccountPlanItem {
  _id: string;
  accountCode: string;
  accountName: string;
  classification: string;
  mainAccountName: string;
}

export interface MukellefItem {
  name: string;
  unvan?: string;
  vkn: string;
  phone?: string;
  email?: string;
  city?: string;
  taxOffice?: string;
  status?: string;
  statusLabel?: string;
  statusColor?: string;
  datasource?: 'own_drive' | 'central_drive' | 'couchdb' | 'duckdb' | string;
  ocr?: 'gemini' | 'python' | 'deepseek' | 'qwen' | 'mistral' | string;
  isSelf?: boolean;
}

export interface SyncStatus {
  ratesStatus: 'offline' | 'syncing' | 'paused' | 'error' | 'connected';
  muhasebeStatus: 'offline' | 'syncing' | 'paused' | 'error' | 'connected';
  lastSyncedAt?: Date;
  errorMessage?: string;
}

@Injectable({
  providedIn: 'root',
})
export class DatabaseService {
  private readonly socketService = inject(SocketService);
  private readonly googleWorkspace = inject(GoogleWorkspaceService);
  readonly version = '2.4.3';
  readonly lastUpdated = '2026-09-09T03:55:00+03:00';

  /**
   * VKN parameter for tenant scoping.
   * Remains completely empty until an authenticated user logs in or a valid session is restored.
   */
  readonly currentVkn = signal<string>('');

  /**
   * Root VKN of the authenticated primary account (e.g. SMMM VKN or primary taxpayer).
   */
  readonly rootVkn = signal<string>('');
  readonly userRole = signal<string>('');

  /**
   * UI View Mode selection:
   * - 'basit': 2-panel Gemini-style view (Sohbet + Fiş/Sonuç)
   * - 'standart': User view (Sohbet, Fatura, Zrapor, Masraf, Diğer, Mizan, Bilanço, Rapor)
   * - 'gelismis': Management/SMMM view (Sohbet, Nakit varlıklar, Duran varlıklar, Masraf, Fatura, Diğer, Mizan, Bilanço, Rapor)
   */
  readonly appViewMode = signal<'basit' | 'standart' | 'gelismis'>(
    (typeof window !== 'undefined' && (localStorage.getItem('ustat_view_mode') as any)) || 'standart'
  );

  setViewMode(mode: 'basit' | 'standart' | 'gelismis'): void {
    this.appViewMode.set(mode);
    if (typeof window !== 'undefined') {
      localStorage.setItem('ustat_view_mode', mode);
    }
  }

  isSmmmUser(): boolean {
    const r = (this.userRole() || '').toUpperCase();
    return r === 'SMMM' || r === 'MALI_MUSAVIR';
  }

  private ratesDb!: PouchDB.Database;
  private muhasebeDb!: PouchDB.Database;

  private ratesSyncHandler: any = null;
  private muhasebeSyncHandler: any = null;
  private ratesChangesHandler: any = null;
  private muhasebeChangesHandler: any = null;

  // Change tracking to prevent redundant parsing when sync has no updates
  private lastMuhasebeUpdateSeq: number | string | null = null;
  private lastRatesUpdateSeq: number | string | null = null;
  private muhasebeDebounceTimer: any = null;
  private ratesDebounceTimer: any = null;
  private isMuhasebeLoading = false;
  private isRatesLoading = false;

  // Reactive State Signals
  readonly syncStatus = signal<SyncStatus>({
    ratesStatus: 'offline',
    muhasebeStatus: 'offline',
  });

  readonly currencyRates = signal<CurrencyRateItem[]>([]);
  readonly customers = signal<CustomerItem[]>([]);
  readonly mukellefler = signal<MukellefItem[]>([]);
  readonly activeMukellef = computed(() => {
    const current = this.currentVkn();
    return this.mukellefler().find((m) => m.vkn === current) || null;
  });
  readonly activeDatasource = computed<'own_drive' | 'central_drive' | 'couchdb' | 'duckdb' | string>(() => {
    return this.activeMukellef()?.datasource || this.getCurrentUserData()?.datasource || 'couchdb';
  });
  readonly activeOcrEngine = computed<'gemini' | 'python' | 'deepseek' | 'qwen' | 'mistral' | string>(() => {
    return this.activeMukellef()?.ocr || this.getCurrentUserData()?.ocr || 'gemini';
  });
  readonly products = signal<ProductItem[]>([]);
  readonly invoices = signal<InvoiceItem[]>([]);
  readonly chatMessages = signal<ChatMessageItem[]>([]);
  readonly accounts = signal<AccountPlanItem[]>([]);
  readonly hesapPlani = signal<HesapPlaniItem[]>([]);
  readonly yevmiyeTransactions = signal<YevmiyeTransactionItem[]>([]);
  readonly originalDocs = signal<UploadedDocumentItem[]>([]);
  readonly tokenUsages = signal<TokenUsageItem[]>([]);
  readonly isAiThinking = signal<boolean>(false);

  constructor() {
    console.log(`[Ustat][DatabaseService][v${this.version}] Service instantiated in IDLE state. ZERO data flow until authenticated.`);
  }

  /**
   * Starts replication and data loading strictly for an authenticated user's VKN.
   * NEVER runs automatically on app load without an authenticated session.
   */
  async startSyncForUser(vkn: string): Promise<void> {
    const cleanVkn = (vkn || '').trim();
    const isValidVkn = /^\d{10,11}$/.test(cleanVkn);
    if (!isValidVkn) {
      console.warn(`[Ustat][DatabaseService] startSyncForUser called with invalid VKN: "${vkn}". Aborting sync.`);
      return;
    }

    console.log(`[Ustat][DatabaseService] Starting CouchDB sync for authenticated VKN: "${cleanVkn}" at ${new Date().toISOString()}`);
    this.currentVkn.set(cleanVkn);
    this.rootVkn.set(cleanVkn);

    try {
      const stored = localStorage.getItem('currentUser');
      if (stored) {
        const u = JSON.parse(stored);
        this.userRole.set(u.userRole || u.type || u.userType || '');
      }
    } catch {}

    await this.initDatabases();
    await this.cleanupForeignDocs();
    this.setupReplication();
    await this.loadAllData();
    await this.fetchMukellefler(cleanVkn);
    this.listenToLocalChanges();
  }

  /**
   * Stops all active replications, cancels listeners, and clears local data signals.
   * Resets the service to complete zero-data idle state.
   */
  stopSyncAndReset(): void {
    console.log('[Ustat][DatabaseService] Stopping all sync handlers and resetting local state to IDLE.');
    if (this.ratesSyncHandler) {
      try { this.ratesSyncHandler.cancel(); } catch (_) {}
      this.ratesSyncHandler = null;
    }
    if (this.muhasebeSyncHandler) {
      try { this.muhasebeSyncHandler.cancel(); } catch (_) {}
      this.muhasebeSyncHandler = null;
    }
    if (this.ratesChangesHandler) {
      try { this.ratesChangesHandler.cancel(); } catch (_) {}
      this.ratesChangesHandler = null;
    }
    if (this.muhasebeChangesHandler) {
      try { this.muhasebeChangesHandler.cancel(); } catch (_) {}
      this.muhasebeChangesHandler = null;
    }

    this.currentVkn.set('');
    this.rootVkn.set('');
    this.userRole.set('');
    this.lastMuhasebeUpdateSeq = null;
    this.lastRatesUpdateSeq = null;
    if (this.muhasebeDebounceTimer) { clearTimeout(this.muhasebeDebounceTimer); this.muhasebeDebounceTimer = null; }
    if (this.ratesDebounceTimer) { clearTimeout(this.ratesDebounceTimer); this.ratesDebounceTimer = null; }
    this.syncStatus.set({ ratesStatus: 'offline', muhasebeStatus: 'offline' });
    this.currencyRates.set([]);
    this.customers.set([]);
    this.mukellefler.set([]);
    this.products.set([]);
    this.invoices.set([]);
    this.chatMessages.set([]);
    this.accounts.set([]);
    this.hesapPlani.set([]);
    this.yevmiyeTransactions.set([]);
    this.originalDocs.set([]);
    this.tokenUsages.set([]);
  }

  /**
   * Clears and resets local PouchDB databases on entry to ensure zero stale IndexedDB cache.
   */
  private async initDatabases(): Promise<void> {
    try {
      if (!this.ratesDb) {
        this.ratesDb = new PouchDB('daily_currency_rates');
      }
      if (!this.muhasebeDb) {
        this.muhasebeDb = new PouchDB('muhasebe');
      }
      console.log('[Ustat][DatabaseService] Persistent local PouchDB instances ready.');
    } catch (err) {
      console.error('[Ustat][DatabaseService] Error initializing local PouchDB:', err);
      if (!this.ratesDb) this.ratesDb = new PouchDB('daily_currency_rates');
      if (!this.muhasebeDb) this.muhasebeDb = new PouchDB('muhasebe');
    }
  }

  /**
   * Sets up 2-way continuous live replication with CouchDB.
   * - 'daily_currency_rates': 1-way live pull replication.
   * - 'muhasebe': high-speed Mango primary range selector replication for [vkn]: documents.
   */
  private setupReplication(): void {
    const vkn = this.currentVkn();
    if (!vkn) {
      console.log('[Ustat][Replication] No authenticated VKN set. Replication is paused/idle.');
      return;
    }

    if (!environment.COUCHDB_URL) {
      console.warn('[Ustat][Replication] COUCHDB_URL is not configured in environment. Running in offline mode.');
      return;
    }

    const remoteDbName = (environment as any).COUCHDB_DATABASE_NAME || (environment.production ? 'muhasebe' : 'muhasebe-test');
    const ratesRemoteUrl = `${environment.COUCHDB_URL}/daily_currency_rates`;
    const muhasebeRemoteUrl = `${environment.COUCHDB_URL}/${remoteDbName}`;

    console.log('[Ustat][Replication] Setting up replication endpoints:', { ratesRemoteUrl, muhasebeRemoteUrl, targetRemoteDb: remoteDbName });

    try {
      const remoteRates = new PouchDB(ratesRemoteUrl, { skip_setup: true });
      const remoteMuhasebe = new PouchDB(muhasebeRemoteUrl, { skip_setup: true });

      // --- 1. REPLICATION: daily_currency_rates (1-way pull from remote) ---
      console.log('[Ustat][Replication] Starting live pull sync for "daily_currency_rates"...');
      this.ratesSyncHandler = PouchDB.replicate(remoteRates, this.ratesDb, {
        live: true,
        retry: true,
      })
        .on('change', (info: any) => {
          const docsChangedCount = info.change?.docs?.length || 0;
          if (docsChangedCount < 1 || info.direction === 'push') {
            return;
          }
          console.log(`[Ustat][Replication][daily_currency_rates] docsChangedCount: ${docsChangedCount} (>= 1). Scheduling parse...`);
          this.syncStatus.update((s) => ({
            ...s,
            ratesStatus: 'syncing',
            lastSyncedAt: new Date(),
          }));
          this.scheduleRatesLoad();
        })
        .on('paused', (err: any) => {
          console.log('[Ustat][Replication][daily_currency_rates] Replication PAUSED (idle or waiting for network):', err || 'Normal idle');
          this.syncStatus.update((s) => ({
            ...s,
            ratesStatus: err ? 'offline' : 'paused',
          }));
        })
        .on('active', () => {
          console.log('[Ustat][Replication][daily_currency_rates] Replication ACTIVE (transferring data)');
          this.syncStatus.update((s) => ({ ...s, ratesStatus: 'syncing' }));
        })
        .on('denied', (err: any) => {
          console.warn('[Ustat][Replication][daily_currency_rates] Replication DENIED:', err);
          this.syncStatus.update((s) => ({ ...s, ratesStatus: 'error', errorMessage: 'Yetki reddedildi' }));
        })
        .on('error', (err: any) => {
          console.error('[Ustat][Replication][daily_currency_rates] Replication ERROR:', err);
          this.syncStatus.update((s) => ({ ...s, ratesStatus: 'error', errorMessage: err?.message || 'Sync hatası' }));
        });

      // --- 2. REPLICATION: muhasebe (HIGH-SPEED MANGO RANGE SELECTOR) ---
      const vkn = this.currentVkn();
      const rootVkn = this.rootVkn();
      console.log(`[Ustat][Replication] Starting live VKN-filtered replication for "muhasebe" with prefix range "${vkn}:" (root: "${rootVkn}")...`);

      const pullSelector = (rootVkn && rootVkn !== vkn)
        ? {
            $or: [
              { _id: { $gte: `${vkn}:`, $lte: `${vkn}:\ufff0` } },
              { _id: { $gte: `${rootVkn}:`, $lte: `${rootVkn}:\ufff0` } },
            ],
          }
        : {
            _id: {
              $gte: `${vkn}:`,
              $lte: `${vkn}:\ufff0`,
            },
          };

      this.muhasebeSyncHandler = PouchDB.sync(this.muhasebeDb, remoteMuhasebe, {
        live: true,
        retry: true,
        pull: {
          filter: '_selector',
          // CouchDB 3.x native Mango selector on primary index (0.05s response time)
          selector: pullSelector,
        },
        push: {
          // Push only documents belonging to this VKN or root SMMM
          filter: (doc: any) =>
            Boolean(
              doc &&
                doc._id &&
                (doc._id.startsWith(`${vkn}:`) || (rootVkn && doc._id.startsWith(`${rootVkn}:`)))
            ),
        },
      })
        .on('change', (info: any) => {
          const docsChangedCount = info.change?.docs?.length || 0;
          if (docsChangedCount < 1 || info.direction === 'push') {
            console.log(`[Ustat][Replication][muhasebe] docsChangedCount: ${docsChangedCount} (direction: ${info.direction}). No remote changes to parse.`);
            return;
          }
          console.log(`[Ustat][Replication][muhasebe] docsChangedCount: ${docsChangedCount} (>= 1). Pull changes detected. Scheduling parse...`);
          this.syncStatus.update((s) => ({
            ...s,
            muhasebeStatus: 'syncing',
            lastSyncedAt: new Date(),
          }));
          this.scheduleMuhasebeLoad();
        })
        .on('paused', (err: any) => {
          console.log('[Ustat][Replication][muhasebe] Replication PAUSED (idle or waiting for network):', err || 'Normal idle');
          this.syncStatus.update((s) => ({
            ...s,
            muhasebeStatus: err ? 'offline' : 'paused',
          }));
        })
        .on('active', () => {
          console.log('[Ustat][Replication][muhasebe] Replication ACTIVE (syncing VKN records)');
          this.syncStatus.update((s) => ({ ...s, muhasebeStatus: 'syncing' }));
        })
        .on('denied', (err: any) => {
          console.warn('[Ustat][Replication][muhasebe] Replication DENIED:', err);
          this.syncStatus.update((s) => ({ ...s, muhasebeStatus: 'error', errorMessage: 'Yetki reddedildi' }));
        })
        .on('error', (err: any) => {
          console.error('[Ustat][Replication][muhasebe] Replication ERROR:', err);
          this.syncStatus.update((s) => ({ ...s, muhasebeStatus: 'error', errorMessage: err?.message || 'Sync hatası' }));
        });
    } catch (err: any) {
      console.warn('[Ustat][Replication] CouchDB remote connection setup failed:', err?.message || err);
      this.syncStatus.update((s) => ({
        ...s,
        ratesStatus: 'offline',
        muhasebeStatus: 'offline',
      }));
    }
  }

  /**
   * Switches the active taxpayer VKN, re-aligns replication filter, and reloads data.
   */
  async switchVkn(newVkn: string): Promise<void> {
    if (!newVkn || newVkn === this.currentVkn()) return;
    console.log(`[Ustat][DatabaseService] Switching active VKN from ${this.currentVkn()} to ${newVkn}...`);
    this.currentVkn.set(newVkn);
    this.lastMuhasebeUpdateSeq = null;
    if (this.muhasebeSyncHandler) {
      try {
        this.muhasebeSyncHandler.cancel();
      } catch (_) {}
    }
    this.setupReplication();
    await this.loadMuhasebeData(true);
  }

  /**
   * Fetches the SMMM's taxpayer clients list from backend /api/companies/:vkn/mukellefler
   * and local PouchDB, caches it to localStorage, and updates the reactive `mukellefler` signal.
   */
  /**
   * Dynamically retrieves authenticated user session data from localStorage.
   * Ensures multi-tenant isolation without hardcoding any user or SMMM titles.
   */
  getCurrentUserData(): { name: string; unvan: string; vkn: string; phone: string; datasource?: string; ocr?: string } {
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        const stored = localStorage.getItem('currentUser');
        if (stored) {
          const u = JSON.parse(stored);
          return {
            name: u.name || '',
            unvan: u.unvan || u.name || u.organization || u.company || '',
            vkn: u.vkn || u.vknTckn || u.VKN || u.taxid || '',
            phone: u.phone || '',
            datasource: u.datasource || '',
            ocr: u.ocr || '',
          };
        }
      }
    } catch {}
    return { name: '', unvan: '', vkn: '', phone: '', datasource: '', ocr: '' };
  }

  async fetchMukellefler(smmmVkn?: string): Promise<MukellefItem[]> {
    const targetVkn = smmmVkn || this.rootVkn() || this.currentVkn();
    if (!targetVkn) return [];

    // 1. Fetch live data from backend endpoint
    try {
      const resp = await fetch(`${environment.apiBaseUrl}/api/companies/${targetVkn}/mukellefler`);
      if (resp.ok) {
        const data = await resp.json();
        if (data.success && Array.isArray(data.mukellefler)) {
          const mapped: MukellefItem[] = data.mukellefler.map((m: any) => ({
            name: m.unvan || m.name || m.vkn,
            vkn: m.vkn,
            phone: m.phone || '',
            email: m.email || '',
            city: m.city || '',
            taxOffice: m.taxOffice || '',
            status: m.status || '',
            statusLabel: m.statusLabel || m.status || '',
            statusColor: m.statusColor || (m.status?.toLowerCase().includes('problem') ? 'red' : 'green'),
            datasource: m.datasource || 'couchdb',
            ocr: m.ocr || 'gemini',
            isSelf: Boolean(m.isSelf),
          }));

          const hasSelf = mapped.some((m) => m.isSelf || m.vkn === targetVkn);
          if (!hasSelf && targetVkn) {
            const curUser = this.getCurrentUserData();
            mapped.unshift({
              name: curUser.unvan || curUser.name || 'Meslek Mensubu Ofisi',
              vkn: targetVkn,
              phone: curUser.phone || '',
              isSelf: true,
            });
          }

          this.mukellefler.set(mapped);
          return mapped;
        }
      }
    } catch (err) {
      console.warn('[Ustat][fetchMukellefler] Remote fetch notice:', err);
    }

    // 2. Query PouchDB local [targetVkn]:mükellef: records
    if (this.muhasebeDb) {
      try {
        const pResult = await this.muhasebeDb.allDocs({
          startkey: `${targetVkn}:mükellef:`,
          endkey: `${targetVkn}:mükellef:\ufff0`,
          include_docs: true,
        });
        const pouchDocs = pResult.rows.map((r) => r.doc as any).filter(Boolean);
        if (pouchDocs.length > 0) {
          const currentList = [...this.mukellefler()];
          for (const doc of pouchDocs) {
            const docVkn = doc.vknTckn || doc.vkn;
            if (docVkn && !currentList.some((m) => m.vkn === docVkn)) {
              currentList.push({
                name: doc.company || doc.name || docVkn,
                vkn: docVkn,
                phone: doc.phone || '',
                email: doc.email || '',
                city: doc.city || '',
                taxOffice: doc.taxOffice || '',
                status: doc.status || '',
                statusLabel: doc.statusLabel || doc.status || '',
                statusColor: doc.statusColor || (doc.status?.toLowerCase().includes('problem') ? 'red' : 'green'),
                isSelf: false,
              });
            }
          }
          this.mukellefler.set(currentList);
          return currentList;
        }
      } catch (_) {}
    }

    return this.mukellefler();
  }

  private scheduleMuhasebeLoad(): void {
    if (this.muhasebeDebounceTimer) {
      clearTimeout(this.muhasebeDebounceTimer);
    }
    this.muhasebeDebounceTimer = setTimeout(() => {
      this.loadMuhasebeData();
    }, 250);
  }

  private scheduleRatesLoad(): void {
    if (this.ratesDebounceTimer) {
      clearTimeout(this.ratesDebounceTimer);
    }
    this.ratesDebounceTimer = setTimeout(() => {
      this.loadRatesData();
    }, 250);
  }

  private listenToLocalChanges(): void {
    if (this.ratesChangesHandler) {
      try { this.ratesChangesHandler.cancel(); } catch (_) {}
      this.ratesChangesHandler = null;
    }
    if (this.muhasebeChangesHandler) {
      try { this.muhasebeChangesHandler.cancel(); } catch (_) {}
      this.muhasebeChangesHandler = null;
    }

    if (this.ratesDb) {
      this.ratesChangesHandler = this.ratesDb
        .changes({ since: 'now', live: true, include_docs: false })
        .on('change', () => {
          this.scheduleRatesLoad();
        });
    }

    if (this.muhasebeDb) {
      this.muhasebeChangesHandler = this.muhasebeDb
        .changes({ since: 'now', live: true, include_docs: false })
        .on('change', () => {
          this.scheduleMuhasebeLoad();
        });
    }
  }

  async loadAllData(): Promise<void> {
    console.log('[Ustat][DatabaseService] loadAllData triggered.');
    await Promise.all([this.loadRatesData(), this.loadMuhasebeData()]);
  }

  /**
   * Loads rates from local PouchDB.
   * Can parse CouchDB daily documents (format with USD_buy, EUR_buy, etc.) or currency_rate items.
   */
  async loadRatesData(force = false): Promise<void> {
    if (this.isRatesLoading) return;
    try {
      if (!this.ratesDb) return;

      const info = await this.ratesDb.info();
      if (!force && this.lastRatesUpdateSeq !== null && info.update_seq === this.lastRatesUpdateSeq) {
        return;
      }

      this.isRatesLoading = true;
      const result = await this.ratesDb.allDocs({ include_docs: true, descending: true });
      this.lastRatesUpdateSeq = info.update_seq;
      const docs = result.rows.map((row) => row.doc as any).filter(Boolean);

      // Check if we have CouchDB date-based docs (e.g. { _id: '2026-07-26', USD_buy: 47.14, ... })
      const dateRateDoc = docs.find((d) => d.USD_buy && d.EUR_buy);

      if (dateRateDoc) {
        console.log(`[Ustat][DatabaseService] Parsing live CouchDB rate document: "${dateRateDoc._id}"`);
        const liveRates: CurrencyRateItem[] = [
          {
            _id: `${dateRateDoc._id}_USD`,
            type: 'currency_rate',
            code: 'USD',
            name: 'Amerikan Doları',
            buying: dateRateDoc.USD_buy || 0,
            selling: dateRateDoc.USD_sell || 0,
            changeRate: 0.24,
            updatedAt: dateRateDoc._id,
          },
          {
            _id: `${dateRateDoc._id}_EUR`,
            type: 'currency_rate',
            code: 'EUR',
            name: 'Euro',
            buying: dateRateDoc.EUR_buy || 0,
            selling: dateRateDoc.EUR_sell || 0,
            changeRate: -0.15,
            updatedAt: dateRateDoc._id,
          },
          {
            _id: `${dateRateDoc._id}_GBP`,
            type: 'currency_rate',
            code: 'GBP',
            name: 'İngiliz Sterlini',
            buying: dateRateDoc.GBP_buy || 0,
            selling: dateRateDoc.GBP_sell || 0,
            changeRate: 0.18,
            updatedAt: dateRateDoc._id,
          },
          {
            _id: `${dateRateDoc._id}_CHF`,
            type: 'currency_rate',
            code: 'CHF',
            name: 'İsviçre Frangı',
            buying: dateRateDoc.CHF_buy || 0,
            selling: dateRateDoc.CHF_sell || 0,
            changeRate: 0.05,
            updatedAt: dateRateDoc._id,
          },
        ];
        this.currencyRates.set(liveRates);
        return;
      }

      // Check standard format
      const standardRates = docs.filter((d) => d.type === 'currency_rate') as CurrencyRateItem[];
      if (standardRates.length > 0) {
        this.currencyRates.set(standardRates);
      } else if (docs.length === 0) {
        console.log('[Ustat][DatabaseService] No rates in local DB, seeding default rates...');
        await this.seedCurrencyRates();
      }
    } catch (err) {
      console.error('[Ustat][DatabaseService] Error loading rates:', err);
    } finally {
      this.isRatesLoading = false;
    }
  }

  /**
   * Cleans any legacy or foreign documents from local IndexedDB that do not belong to current VKN.
   * Guarantees that local database holds ONLY documents for our authenticated user.
   */
  async cleanupForeignDocs(): Promise<void> {
    const vkn = this.currentVkn();
    if (!vkn || !this.muhasebeDb) return;

    try {
      console.log(`[Ustat][DatabaseService] Verifying local database documents. Expected VKN prefix: "${vkn}:"...`);
      const allDocs = await this.muhasebeDb.allDocs({ include_docs: true });
      const foreignDocs = allDocs.rows
        .map((r) => r.doc as any)
        .filter((doc) => {
          if (!doc || !doc._id) return false;
          if (doc._id.startsWith('_design/')) return false;
          return !doc._id.startsWith(`${vkn}:`);
        });

      if (foreignDocs.length > 0) {
        console.warn(`[Ustat][DatabaseService] Found ${foreignDocs.length} foreign documents from another VKN in local database. Purging them...`);
        const toDelete = foreignDocs.map((doc) => ({
          _id: doc._id,
          _rev: doc._rev,
          _deleted: true,
        }));
        await this.muhasebeDb.bulkDocs(toDelete);
        console.log(`[Ustat][DatabaseService] Cleaned ${foreignDocs.length} foreign VKN documents from local database.`);
      } else {
        console.log('[Ustat][DatabaseService] Local database verified: Holds ONLY documents for our user.');
      }
    } catch (err) {
      console.error('[Ustat][DatabaseService] Error during cleanupForeignDocs:', err);
    }
  }

  /**
   * Loads documents from local PouchDB matching the current VKN prefix.
   */
  async loadMuhasebeData(force = false): Promise<void> {
    if (this.isMuhasebeLoading) return;
    try {
      const vkn = this.currentVkn();
      if (!vkn || !this.muhasebeDb) {
        return;
      }

      // Check if database has changed since last parse
      const info = await this.muhasebeDb.info();
      if (!force && this.lastMuhasebeUpdateSeq !== null && info.update_seq === this.lastMuhasebeUpdateSeq) {
        console.log(`[Ustat][DatabaseService] loadMuhasebeData: No database changes (seq: ${info.update_seq}). Skipping parse.`);
        return;
      }

      this.isMuhasebeLoading = true;
      console.log(`[Ustat][DatabaseService] loadMuhasebeData: Parsing documents for VKN "${vkn}:" (seq: ${info.update_seq})...`);

      // Query local documents strictly for the active VKN range
      const result = await this.muhasebeDb.allDocs({
        startkey: `${vkn}:`,
        endkey: `${vkn}:\ufff0`,
        include_docs: true,
      });

      this.lastMuhasebeUpdateSeq = info.update_seq;

      const vknDocs = result.rows.map((row) => row.doc as any).filter(Boolean);
      console.log(`[Ustat][DatabaseService] loadMuhasebeData: Found ${vknDocs.length} documents for VKN "${vkn}:"`);

      // Parse custom app entities
      const customersList = vknDocs.filter(
        (d) =>
          d.type === 'customer' ||
          d.type === 'mükellef' ||
          d.type === 'mukellef' ||
          d._id?.includes(':mükellef:') ||
          d._id?.includes(':mukellef:') ||
          (d.type === 'account' &&
            (d.accountCode === '120' ||
              d.account === '120' ||
              d.subType === 'customer' ||
              !d.accountCode))
      ) as CustomerItem[];
      const productsList = vknDocs.filter((d) => d.type === 'product') as ProductItem[];
      const invoicesList = vknDocs.filter((d) => d.type === 'invoice') as InvoiceItem[];
      const rawChatList = vknDocs.filter((d) => d.type === 'chat_message') as ChatMessageItem[];
      const chatList: ChatMessageItem[] = [];
      for (const msg of rawChatList) {
        if (
          msg._id &&
          (msg._id.endsWith(':chat_2') ||
            msg._id.endsWith(':chat_3') ||
            msg.text?.includes('FTR-2026-00143') ||
            msg.text?.includes('Kaya Mimarlık A.Ş.'))
        ) {
          try {
            await this.muhasebeDb.remove(msg._id, (msg as any)._rev);
          } catch {}
        } else {
          chatList.push(msg);
        }
      }
      const originalDocsList = vknDocs.filter(
        (d) =>
          d.type === 'original_doc' ||
          d.type === 'vergi_levhasi' ||
          d.type === 'vergi_levhası' ||
          d._id?.includes(':file:')
      ) as UploadedDocumentItem[];
      const tokenList = vknDocs.filter((d) => d.type === 'token_usage') as TokenUsageItem[];

      // Parse Hesap Planı (hp) documents (e.g. [vkn]:hp:108.01)
      const hpDocs = vknDocs.filter((d) => d._id && (d._id.includes(':hp:') || d.id?.startsWith('hp:'))) as HesapPlaniItem[];
      this.hesapPlani.set(hpDocs);

      // Parse Yevmiye Fişleri (yev) documents (e.g. [vkn]:yev:[voucherNumber]:[seq])
      const yevDocs = vknDocs.filter(
        (d) => d.type === 'yevmiye' || (d._id && d._id.includes(':yev:'))
      ) as YevmiyeTransactionItem[];
      this.yevmiyeTransactions.set(yevDocs);

      const parsedAccounts: AccountPlanItem[] = hpDocs.map((d) => ({
        _id: d._id,
        accountCode: d.accountCode || d.id?.replace('hp:', '') || '',
        accountName: d.accountName || '',
        classification: d.classification || '',
        mainAccountName: d.mainAccountName || '',
      }));
      this.accounts.set(parsedAccounts);

      // -------------------------------------------------------------
      // PLUGGABLE DATASOURCE: central_drive | own_drive | couchdb | duckdb
      // If datasource is 'central_drive', feed UI display directly
      // from the Google Drive Sheet (/api/companies/:vkn/yevmiye/sheet).
      // -------------------------------------------------------------
      const activeCompany = this.mukellefler().find((m) => m.vkn === vkn);
      let activeDs = activeCompany?.datasource;
      if (!activeDs) {
        try {
          const pResp = await fetch(`${environment.apiBaseUrl}/api/companies/${vkn}/params`);
          if (pResp.ok) {
            const pData = await pResp.json();
            if (pData.params?.datasource) {
              activeDs = pData.params.datasource;
            }
          }
        } catch (_) {}
      }

      if (activeDs === 'central_drive') {
        try {
          console.log(`[Ustat][DatabaseService] Active datasource is "central_drive" for VKN ${vkn}. Loading yevmiye transactions from Google Drive Sheet...`);
          const sheetResp = await fetch(`${environment.apiBaseUrl}/api/companies/${vkn}/yevmiye/sheet`);
          if (sheetResp.ok) {
            const sheetData = await sheetResp.json();
            if (sheetData.success && Array.isArray(sheetData.rows) && sheetData.rows.length > 0) {
              const mappedSheetTxs: YevmiyeTransactionItem[] = sheetData.rows.map((r: any, idx: number) => {
                const voucherNumber = String(r['Fiş No'] || '');
                const orderNumber = r['Madde / Sıra No'] || idx + 1;
                const date = r['Yevmiye Tarihi'] || '';
                const accountCode = String(r['Hesap Kodu'] || '');
                const accountName = String(r['Hesap Adı'] || '');
                const debit = parseFloat(r['Borç (₺)']) || 0;
                const credit = parseFloat(r['Alacak (₺)']) || 0;
                const desc = r['Açıklama'] || '';
                const docType = r['Belge Türü'] || '';
                const docNo = r['Belge No'] || '';
                const docDate = r['Belge Tarihi'] || '';
                const counterpartTitle = r['Karşı Taraf (Cari)'] || '';
                const counterpartTaxId = r['Karşı Taraf VKN/TCKN'] || '';
                const paymentMethod = r['Ödeme Yöntemi'] || '';
                const classification = r['Sınıflandırma'] || '';
                const mainAccountCode = r['Ana Hesap'] || accountCode.split('.')[0] || '';
                const id = r['Kayıt ID'] || `${vkn}:yev:${date.replace(/-/g, '')}:${voucherNumber}:${orderNumber}`;

                return {
                  _id: id,
                  id: id,
                  type: 'yevmiye',
                  voucherNumber,
                  orderNumber,
                  date,
                  accountCode,
                  accountName,
                  debit,
                  credit,
                  description: desc,
                  documentType: docType,
                  documentNumber: docNo,
                  documentDate: docDate,
                  counterpartTitle,
                  counterpartTaxId,
                  paymentMethod,
                  classification,
                  mainAccountCode,
                  currency: r['Döviz Cinsi'] || 'TRY',
                  currencyDebt: parseFloat(r['Döviz Borç']) || 0,
                  currencyCredit: parseFloat(r['Döviz Alacak']) || 0,
                  currencyRate: parseFloat(r['Döviz Kuru']) || 1,
                };
              });

              this.yevmiyeTransactions.set(mappedSheetTxs);

              // Derive Hesap Planı accounts from Sheet rows
              const accountMap = new Map<string, AccountPlanItem>();
              for (const tx of mappedSheetTxs) {
                if (tx.accountCode && !accountMap.has(tx.accountCode)) {
                  accountMap.set(tx.accountCode, {
                    _id: `${vkn}:hp:${tx.accountCode}`,
                    accountCode: tx.accountCode,
                    accountName: tx.accountName,
                    classification: tx.classification || '',
                    mainAccountName: tx.accountName,
                  });
                }
              }
              const sheetAccounts = Array.from(accountMap.values()).sort((a, b) =>
                a.accountCode.localeCompare(b.accountCode)
              );
              if (sheetAccounts.length > 0) {
                this.accounts.set(sheetAccounts);
                this.hesapPlani.set(
                  sheetAccounts.map(
                    (a) =>
                      ({
                        _id: a._id,
                        id: a.accountCode,
                        accountCode: a.accountCode,
                        accountName: a.accountName,
                        classification: a.classification,
                        type: 'hesap_plani',
                      } as any)
                  )
                );
              }

              console.log(
                `[Ustat][DatabaseService] Successfully populated UI from Central Drive Google Sheet (${mappedSheetTxs.length} entries, ${sheetAccounts.length} accounts)`
              );
            }
          }
        } catch (sheetErr) {
          console.warn('[Ustat][DatabaseService] Central Drive sheet fetch notice:', sheetErr);
        }
      }

      console.log('[Ustat][DatabaseService] Parsed entities for VKN:', {
        customersCount: customersList.length,
        productsCount: productsList.length,
        invoicesCount: invoicesList.length,
        chatMessagesCount: chatList.length,
        hesapPlaniCount: hpDocs.length,
        yevmiyeCount: yevDocs.length,
        originalDocsCount: originalDocsList.length,
        tokenUsageCount: tokenList.length,
      });

      if (vknDocs.length === 0) {
        console.log(`[Ustat][DatabaseService] No local records for VKN ${vkn}. Starting with clean database (seeding disabled).`);
        // await this.seedMuhasebeData(); // seedMuhasebeData devre dışı bırakıldı - ilerde lazım olursa açılabilir
        this.customers.set([]);
        this.products.set([]);
        this.invoices.set([]);
        this.chatMessages.set([]);
        this.originalDocs.set([]);
        this.tokenUsages.set([]);
      } else {
        if (customersList.length > 0) {
          this.customers.set(
            customersList.sort((a, b) => {
              const dtA = a.createdAt || a._id || '';
              const dtB = b.createdAt || b._id || '';
              return dtB.localeCompare(dtA);
            })
          );
        }
        if (productsList.length > 0) {
          this.products.set(
            productsList.sort((a, b) => {
              const dtA = a.createdAt || a._id || '';
              const dtB = b.createdAt || b._id || '';
              return dtB.localeCompare(dtA);
            })
          );
        }
        if (invoicesList.length > 0) {
          this.invoices.set(
            invoicesList.sort((a, b) => {
              const dtA = a.date || a.createdAt || '';
              const dtB = b.date || b.createdAt || '';
              return dtB.localeCompare(dtA);
            })
          );
        }
        if (chatList.length > 0) {
          this.chatMessages.set(
            chatList.sort((a, b) => {
              const dtA = a.createdAt || a.timestamp || '';
              const dtB = b.createdAt || b.timestamp || '';
              return dtA.localeCompare(dtB);
            })
          );
        }
        if (originalDocsList.length > 0) {
          this.originalDocs.set(
            originalDocsList.sort((a, b) => (b.uploadDateTime || '').localeCompare(a.uploadDateTime || ''))
          );
        } else {
          // const sampleDocs = this.getInitialOriginalDocs(vkn);
          // this.originalDocs.set(
          //   sampleDocs.sort((a, b) => (b.uploadDateTime || '').localeCompare(a.uploadDateTime || ''))
          // );
          this.originalDocs.set([]);
        }
        if (tokenList.length > 0) {
          this.tokenUsages.set(
            tokenList.sort((a, b) => (b.dateTime || '').localeCompare(a.dateTime || ''))
          );
        } else {
          // const sampleTokens = this.getInitialTokenUsages(vkn);
          // this.tokenUsages.set(
          //   sampleTokens.sort((a, b) => (b.dateTime || '').localeCompare(a.dateTime || ''))
          // );
          this.tokenUsages.set([]);
        }
      }
    } catch (err) {
      console.error('[Ustat][DatabaseService] Error loading muhasebe data:', err);
    } finally {
      this.isMuhasebeLoading = false;
    }
  }

  // --- CRUD HELPERS ---

  async addDocument(db: 'rates' | 'muhasebe', doc: any): Promise<any> {
    const targetDb = db === 'rates' ? this.ratesDb : this.muhasebeDb;

    if (db === 'muhasebe') {
      doc.vkn = this.currentVkn();
      if (!doc._id || !doc._id.startsWith(`${this.currentVkn()}:`)) {
        doc._id = `${this.currentVkn()}:${doc.type || 'doc'}_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`;
      }
    } else if (!doc._id) {
      doc._id = `${doc.type || 'doc'}_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`;
    }

    console.log(`[Ustat][DatabaseService] addDocument to [${db}]:`, { id: doc._id, type: doc.type, vkn: doc.vkn });
    const response = await targetDb.put(doc);
    console.log(`[Ustat][DatabaseService] Document saved response:`, response);

    if (doc.type === 'chat_message') {
      this.chatMessages.update((prev) => {
        const exists = prev.some((m) => m._id === doc._id);
        return exists ? prev : [...prev, doc];
      });
    }

    if (db === 'rates') await this.loadRatesData(true);
    else await this.loadMuhasebeData(true);
    return response;
  }

  async updateDocument(db: 'rates' | 'muhasebe', doc: any): Promise<any> {
    console.log(`[Ustat][DatabaseService] updateDocument in [${db}]:`, { id: doc._id, rev: doc._rev });
    const targetDb = db === 'rates' ? this.ratesDb : this.muhasebeDb;
    const response = await targetDb.put(doc);
    if (db === 'rates') await this.loadRatesData(true);
    else await this.loadMuhasebeData(true);
    return response;
  }

  async deleteDocument(db: 'rates' | 'muhasebe', docId: string, docRev: string): Promise<any> {
    console.log(`[Ustat][DatabaseService] deleteDocument from [${db}]:`, { id: docId, rev: docRev });
    const targetDb = db === 'rates' ? this.ratesDb : this.muhasebeDb;
    const response = await targetDb.remove(docId, docRev);
    if (db === 'rates') await this.loadRatesData(true);
    else await this.loadMuhasebeData(true);
    return response;
  }

  // --- Panel Specific Actions ---

  async sendChatMessage(
    text: string,
    sender: 'user' | 'assistant' = 'user',
    attachments?: Array<{
      fileName: string;
      fileSizeFormatted?: string;
      fileType?: string;
      file?: File;
      dataUrl?: string;
    }>
  ): Promise<void> {
    console.log(`[Ustat][Chat] 📨 MESAJ GÖNDERİLİYOR! [sender: ${sender}]: "${text}" (Ekli Dosyalar: ${attachments?.length || 0}):`, attachments?.map((a) => `${a.fileName} (${a.fileSizeFormatted || ''})`));
    const now = new Date();
    const pad = (n: number) => n.toString().padStart(2, '0');
    const nowFormatted = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())} ${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}`;
    const timeShort = now.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });

    // Store clean attachments metadata in message
    const msgAttachments = attachments?.map((a) => ({
      fileName: a.fileName,
      fileSizeFormatted: a.fileSizeFormatted,
      fileType: a.fileType,
      dataUrl: a.dataUrl,
    }));

    const msg: ChatMessageItem = {
      type: 'chat_message',
      vkn: this.currentVkn(),
      sender,
      text,
      attachments: msgAttachments && msgAttachments.length > 0 ? msgAttachments : undefined,
      timestamp: timeShort,
      createdAt: nowFormatted,
    };
    // Chat message saving to PouchDB commented out - display only in UI memory
    // await this.addDocument('muhasebe', msg);
    this.chatMessages.update((prev) => [...prev, msg]);

    if (sender === 'user') {
      this.isAiThinking.set(true);
      setTimeout(async () => {
        let replyText = '';
        let structuredData: any = undefined;
        let tokenUsage: any = undefined;

        try {
          const res = await this.queryGeminiOrAssistant(text, attachments);
          replyText = res.text;
          structuredData = res.structuredData;
          tokenUsage = res.tokenUsage;
        } catch (err) {
          console.warn('[Ustat][Chat] AI query error, falling back to local assistant:', err);
          replyText = this.generateAssistantReplyWithAttachments(text, attachments);
        } finally {
          this.isAiThinking.set(false);
        }

        console.log(`[Ustat][Chat] Assistant generated reply:`, { replyLength: replyText.length, hasStructured: !!structuredData, tokenUsage });
        const repNow = new Date();
        const repNowFormatted = `${repNow.getFullYear()}-${pad(repNow.getMonth() + 1)}-${pad(repNow.getDate())} ${pad(repNow.getHours())}:${pad(repNow.getMinutes())}:${pad(repNow.getSeconds())}`;
        const replyMsg: ChatMessageItem = {
          type: 'chat_message',
          vkn: this.currentVkn(),
          sender: 'assistant',
          text: replyText,
          structuredData,
          tokenUsage,
          timestamp: repNow.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' }),
          createdAt: repNowFormatted,
        };
        // Chat assistant response saving to PouchDB commented out - display only in UI memory
        // await this.addDocument('muhasebe', replyMsg);
        this.chatMessages.update((prev) => [...prev, replyMsg]);

        // Auto-record token consumption for Asistan Sohbet / Evrak Analiz
        const inTok = tokenUsage?.tokensIn || Math.max(25, Math.round((text.length + (attachments?.length || 0) * 150) / 3.4));
        const outTok = tokenUsage?.tokensOut || Math.max(40, Math.round(replyText.length / 3.4));
        await this.recordTokenUsage(attachments && attachments.length > 0 ? 'Evrak Analiz' : 'Asistan Sohbet', inTok, outTok, tokenUsage?.model || 'gemini-2.5-flash');
      }, 400);
    }
  }

  private async queryGeminiOrAssistant(
    userPrompt: string,
    attachments?: Array<{
      fileName: string;
      fileSizeFormatted?: string;
      fileType?: string;
      file?: File;
      dataUrl?: string;
    }>
  ): Promise<{ text: string; structuredData?: any; tokenUsage?: any }> {
    const ocrConfig = environment.aiOcrChatConfig;
    if (!ocrConfig) {
      return { text: this.generateAssistantReplyWithAttachments(userPrompt, attachments) };
    }

    let tempApiKey: string | null = null;

    try {
      console.log(`[Ustat][AI] 🤖 AI EVRAK ANALİZİ BAŞLATILIYOR! Ek Sayısı: ${attachments?.length || 0}, Dosyalar:`, attachments?.map((a) => `${a.fileName} (${a.fileSizeFormatted || ''}, ${a.fileType || ''})`));
      tempApiKey = await this.socketService.requestApiKey({
        company_id: this.currentVkn(),
        action: 'Prompt Evrak Analiz',
      });

      if (!tempApiKey) {
        console.warn('[Ustat][AI] Sunucudan Socket.IO ile API anahtarı alınamadı, yerel asistan yanıtı üretiliyor.');
        return { text: this.generateAssistantReplyWithAttachments(userPrompt, attachments) };
      }

      const parts: any[] = [];

      // Multimodal file processing
      if (attachments && attachments.length > 0) {
        for (const att of attachments) {
          if (att.file) {
            const ext = (att.fileName.split('.').pop() || '').toLowerCase();
            if (['csv', 'xml', 'json', 'txt'].includes(ext)) {
              try {
                const fileContent = await att.file.text();
                parts.push({
                  text: `\n--- [YÜKLENEN BELGE: ${att.fileName} (${att.fileSizeFormatted || ''})] ---\n${fileContent}\n--- [BELGE SONU] ---\n`,
                });
              } catch (err) {
                console.warn('[Ustat][AI] Error reading text file:', err);
              }
            } else {
              // Read binary as Base64 for images, PDFs, Excel/Word
              try {
                const base64Data = await new Promise<string>((resolve) => {
                  const reader = new FileReader();
                  reader.onload = () => {
                    const res = reader.result as string;
                    const b64 = res.split(',')[1] || '';
                    resolve(b64);
                  };
                  reader.onerror = () => resolve('');
                  reader.readAsDataURL(att.file!);
                });

                if (base64Data) {
                  let mime = att.file.type || 'application/octet-stream';
                  if (ext === 'pdf') mime = 'application/pdf';
                  else if (['jpg', 'jpeg'].includes(ext)) mime = 'image/jpeg';
                  else if (ext === 'png') mime = 'image/png';
                  else if (ext === 'webp') mime = 'image/webp';
                  else if (ext === 'xlsx') mime = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
                  else if (ext === 'xls') mime = 'application/vnd.ms-excel';

                  parts.push({
                    inlineData: {
                      mimeType: mime,
                      data: base64Data,
                    },
                  });
                }
              } catch (err) {
                console.warn('[Ustat][AI] Error reading binary file:', err);
              }
            }
          }
        }
      }

      const fileNamesList = (attachments || []).map((a) => a.fileName).filter(Boolean).join(', ');
      const textPrompt = `${userPrompt || 'Eklenen mali evrakı dikkatle analiz et, tüm alanları oku ve zorunlu JSON şemasına göre standart veri yapısında dön.'}${fileNamesList ? `\nYüklenen Dosya Adı: ${fileNamesList}` : ''}`;
      parts.push({ text: textPrompt });

      const activeCompany = this.activeMukellef();
      const curUser = this.getCurrentUserData();
      const isSelfAccount = Boolean(activeCompany?.isSelf) || this.isSmmmUser() || (this.rootVkn() && this.currentVkn() === this.rootVkn());
      const activeUnvan = (activeCompany?.name && activeCompany.name !== 'SMMM Ofisi (Kendisi)')
        ? activeCompany.name
        : (curUser.unvan || curUser.name || 'Mükellef');
      const activeVkn = this.currentVkn() || curUser.vkn;

      const systemPrompt = `Sen Ustat Mobile profesyonel mali müşavirlik ve muhasebe asistanısın.
AKTİF SEÇİLİ MÜKELLEF / ŞİRKET: "${activeUnvan}" (VKN: ${activeVkn})${isSelfAccount ? ` - [DİKKAT: Bu hesap oturum açan yetkili kullanıcının kendi öz mükellefiyetidir/ofisidir ("${activeUnvan}"). Belgede geçen bu ünvan tam eşleşmedir, şirket uyuşmazlığı DEĞİLDİR.]` : ''}.
GÖREVİN: Kullanıcının yüklediği her türlü mali evrakı (Yevmiye Defteri, Hesap Planı, Mizan, Fatura, Fiş, Z Raporu, Banka Ekstresi, Cari Mutabakat, Stok/Fiyat Listesi, Sözleşme, Beyanname vb.) en yüksek dikkatle analiz edip yapılandırılmış veriye dönüştürmektir.

KRİTİK KONTROL KURALLARI:
1. BELGE TÜRÜ DOĞRULAMA:
   - "YEVMİYE DEFTERİ", "MADDE NO", "HESAP KODU / BORÇ / ALACAK" içeren yevmiye dökümlerini MUTLAKA "documentType": "YEVMİYE_DEFTERI" olarak belirle (Asla DIGER yapma).
   - "HESAP PLANI", "ANA HESAP / ALT HESAP" dökümlerini "documentType": "HESAP_PLANI" olarak belirle.
   - "MİZAN", "BORÇ / ALACAK BAKİYE" dökümlerini "documentType": "MIZAN" olarak belirle.
   - "Z RAPORU", "GÜNLÜK SATIŞ FİŞİ" dökümlerini "documentType": "Z_RAPORU" olarak belirle.
   - "PERAKENDE SATIŞ FİŞİ", "ÖKC FİŞİ", "POS FİŞİ / SLİP", "YAZARKASA FİŞİ", "E-ARŞİV FİŞİ" (Örn: market, akaryakıt, fırın, yemek, otopark fişleri) gibi harcama/masraf belgelerini MUTLAKA "documentType": "FIS" olarak belirle.

2. ŞİRKET VE VERİ DOĞRULAMA (ASLA ENGELLEME/REDDETME YAPMA):
   - Belgenin ait olduğu şirket / mükellef ünvanını ve dosya adını tespit et.
   - DOSYA ADINA VEYA CARİ HESAP/AÇIKLAMA SATIRLARINA BAKARAK ASLA BELGEYİ REDDETME ("companyMismatch": false, "status": "success").
   - Farklı muhasebe yazılımlarından dışa aktarılan dosyalarda tüm veriyi eksiksiz oku.
   - Yevmiye Defteri, Fatura, Fiş veya Mizan içerisindeki TÜM FİŞLERİ (maddeleri) ve TÜM SATIRLARI kuruşu kuruşuna dengeli (Borç = Alacak) olarak "journal_entries" dizisi içinde yapılandır. Asla satırları budama veya 2 satıra indirgeme.

ZORUNLU KURAL: Çıktını SADECE ve MUTLAKA geçerli bir JSON nesnesi olarak üret. JSON dışında hiçbir metin, önsöz veya kapanış cümlesi ekleme.

STANDART VERİ YAPISI (JSON SCHEMA):
{
  "status": "success",
  "documentType": "YEVMİYE_DEFTERI | HESAP_PLANI | MIZAN | FATURA | FIS | Z_RAPORU | BANKA_EKSTRESI | CARI_MUTABAKAT | STOK_LISTESI | SOZLESME | BEYANNAME | VERGI_LEVHASI | DIGER",
  "companyMismatch": false,
  "detectedCompany": null,
  "rejectionReason": null,
  "confidence": 0.95,
  "parties": {
    "sender": {
      "name": "Gönderici / Şirket / Düzenleyen Ünvanı",
      "vknTckn": "10 Haneli VKN veya 11 Haneli TCKN",
      "taxOffice": "Vergi Dairesi",
      "address": "Açık Adres",
      "phone": "Telefon",
      "email": "E-posta"
    },
    "receiver": {
      "name": "Alıcı / Müşteri / Mükellef Ünvanı",
      "vknTckn": "Alıcı VKN/TCKN (10 Haneli VKN veya 11 Haneli TCKN)",
      "taxOffice": "Vergi Dairesi",
      "address": "Açık Adres",
      "naceCodes": [
        { "code": "Faaliyet Kodu", "description": "Faaliyet Açıklaması", "isPrimary": true }
      ]
    }
  },
  "financials": {
    "documentNo": "Belge No / Yevmiye Madde No",
    "date": "YYYY-MM-DD",
    "dueDate": "YYYY-MM-DD",
    "currency": "TRY | USD | EUR",
    "subtotal": 0.0,
    "vatTotal": 0.0,
    "vatBreakdown": [
      { "rate": 20, "base": 0.0, "amount": 0.0 }
    ],
    "discounts": 0.0,
    "grandTotal": 0.0,
    "paymentMethod": "NAKIT | KREDI_KARTI | HAVALE_EFT | ACIK_HESAP"
  },
  "journal_entries": [
    {
      "voucherNumber": "00001",
      "date": "YYYY-MM-DD",
      "documentType": "Virman | Mahsup | Fatura | Fiş | Z Raporu",
      "description": "Fiş Açıklaması",
      "entries": [
        {
          "accountCode": "656.10",
          "accountName": "KUR FARKI GID.",
          "description": "Satır Açıklaması",
          "debit": 60267.94,
          "credit": 0.0
        }
      ]
    }
  ],
  "items": [
    {
      "lineNo": 1,
      "code": "Hesap Kodu / Mal Kodu",
      "name": "Açıklama / Hesap Adı",
      "quantity": 1,
      "unit": "ADET | KG | vb.",
      "unitPrice": 0.0,
      "vatRate": 20,
      "vatAmount": 0.0,
      "totalAmount": 0.0
    }
  ],
  "suggestedAccountingAccounts": [
    { "accountCode": "Hesap Kodu", "accountName": "Hesap Adı", "type": "BORC | ALACAK", "amount": 0.0 }
  ],
  "taxDeclarations": [
    { "year": 2023, "declaredMatrah": 532553.77, "accruedTax": 133138.44 }
  ],
  "tokenUsage": {
    "tokensIn": 0,
    "tokensOut": 0,
    "totalTokens": 0,
    "model": "gemini-3.1-flash-lite"
  },
  "analysis": {
    "summary": "Belgenin kısa Türkçe özeti ve tespit edilen detaylar",
    "warnings": [],
    "missingFields": []
  }
}
Belgede bulunmayan alanlar için null veya boş liste ver.`;

      const restUrl = `${ocrConfig.endpoint}?key=${tempApiKey}`;
      const fallbackUrl = ocrConfig.fallbackEndpoint ? `${ocrConfig.fallbackEndpoint}?key=${tempApiKey}` : '';

      const payload = JSON.stringify({
        contents: [{ role: 'user', parts }],
        systemInstruction: { parts: [{ text: systemPrompt }] },
        generationConfig: {
          responseMimeType: 'application/json',
        },
      });

      let res = await fetch(restUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: payload,
      });

      if (!res.ok) {
        const errText = await res.text();
        console.error(`[Ustat][AI] Primary endpoint status ${res.status}:`, errText);
        if (fallbackUrl) {
          console.warn(`[Ustat][AI] Attempting fallback endpoint: ${fallbackUrl}...`);
          res = await fetch(fallbackUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: payload,
          });
          if (!res.ok) {
            const fbErrText = await res.text();
            console.error(`[Ustat][AI] Fallback endpoint status ${res.status}:`, fbErrText);
          }
        }
      }

      if (res.ok) {
        const json = await res.json();
        const rawText = json.candidates?.[0]?.content?.parts?.[0]?.text;
        const usage = json.usageMetadata;
        const inTok = usage?.promptTokenCount || Math.max(35, Math.round(((userPrompt || '').length + (attachments?.length || 0) * 220) / 3.4));
        const outTok = usage?.candidatesTokenCount || Math.max(50, Math.round((rawText?.length || 0) / 3.4));
        const totalTok = usage?.totalTokenCount || (inTok + outTok);
        const tokenUsage = {
          tokensIn: inTok,
          tokensOut: outTok,
          totalTokens: totalTok,
          model: ocrConfig.model || 'gemini-3.1-flash-lite',
        };

        if (rawText) {
          try {
            const cleanJsonStr = rawText.replace(/```json\s*/g, '').replace(/```\s*/g, '').trim();
            const parsed = JSON.parse(cleanJsonStr);
            parsed.tokenUsage = tokenUsage;

            // Display raw structured JSON data returned from AI
            console.log('[Ustat][AI] 📄 DOKÜMAN JSON VERİSİ (Gemini API):', parsed);

            // Vergi Levhası Mükellef / SMMM Otomatik İşlemi (Asla şirket uyuşmazlığı nedeniyle reddedilmez)
            const isVergiLevha = parsed.documentType === 'VERGI_LEVHASI' ||
              (parsed.analysis?.summary || '').toLowerCase().includes('vergi levha');
            if (isVergiLevha) {
              parsed.companyMismatch = false;
              parsed.status = 'success';
              parsed.rejectionReason = null;
            }

            if (!parsed.companyMismatch && parsed.status !== 'rejected') {
              await this.processVergiLevhasiIfApplicable(parsed, attachments);
              await this.processFisIfApplicable(parsed, attachments);
            }

            const humanText = this.formatStructuredResponse(parsed);
            return { text: humanText, structuredData: parsed, tokenUsage };
          } catch {
            return { text: rawText.trim(), tokenUsage };
          }
        }
      }
    } catch (netErr) {
      console.warn('[Ustat][AI] Network request failed:', netErr);
    } finally {
      // STRICT SECURITY RULE: Delete API key from memory immediately after send
      tempApiKey = null;
    }

    return { text: this.generateAssistantReplyWithAttachments(userPrompt, attachments) };
  }

  private formatStructuredResponse(data: any): string {
    const isRejected = data.status === 'rejected' || Boolean(data.companyMismatch);
    const docType = data.documentType || 'Belge';

    if (isRejected) {
      const detected = data.detectedCompany || data.parties?.sender?.name || 'Farklı Şirket';
      const activeUnvan = this.activeMukellef()?.name || this.currentVkn();
      const reason = data.rejectionReason || `Yüklenen evraktaki şirket ("${detected}"), aktif seçili mükellef ("${activeUnvan}") ile uyuşmuyor.`;
      const tokenInfo = data.tokenUsage ? `\n• Token: 📥 ${data.tokenUsage.tokensIn} In | 📤 ${data.tokenUsage.tokensOut} Out` : '';

      return `⛔ **İŞLEM REDDEDİLDİ: ŞİRKET ÜNVANI UYUŞMAZLIĞI**\n• Tespit Edilen Belge Türü: **${docType}**\n• Belgedeki Şirket: **${detected}**\n• Aktif Mükellef: **${activeUnvan} (VKN: ${this.currentVkn()})**\n• Gerekçe: ${reason}${tokenInfo}`;
    }
    const summary = data.analysis?.summary || 'Belge başarıyla analiz edildi.';
    const docNo = data.financials?.documentNo ? `\n• Belge No: ${data.financials.documentNo}` : '';
    const date = data.financials?.date ? `\n• Tarih: ${data.financials.date}` : '';
    const sender = data.parties?.sender?.name ? `\n• Düzenleyen: ${data.parties.sender.name}${data.parties.sender.vknTckn ? ` (VKN/TCKN: ${data.parties.sender.vknTckn})` : ''}` : '';
    const receiver = data.parties?.receiver?.name ? `\n• Alıcı: ${data.parties.receiver.name}${data.parties.receiver.vknTckn ? ` (VKN: ${data.parties.receiver.vknTckn})` : ''}` : '';
    const rawNace = data.parties?.receiver?.naceCodes || data.parties?.sender?.naceCodes;
    let naceInfo = '';
    if (Array.isArray(rawNace) && rawNace.length > 0) {
      naceInfo = `\n• Faaliyet Kodları (NACE): ` + rawNace.map((n: any) => typeof n === 'object' ? `${n.code}${n.description ? ` (${n.description})` : ''}` : String(n)).join(', ');
    }
    const rawTaxDecl = data.taxDeclarations;
    let taxDeclInfo = '';
    if (Array.isArray(rawTaxDecl) && rawTaxDecl.length > 0) {
      taxDeclInfo = `\n• Vergi Beyanları (Yıl / Matrah / Tahakkuk):\n` + rawTaxDecl.map((t: any) => `  - ${t.year}: Matrah: ${typeof t.declaredMatrah === 'number' ? '₺' + t.declaredMatrah.toLocaleString('tr-TR', { minimumFractionDigits: 2 }) : t.declaredMatrah} | Tahakkuk: ${typeof t.accruedTax === 'number' ? '₺' + t.accruedTax.toLocaleString('tr-TR', { minimumFractionDigits: 2 }) : t.accruedTax}`).join('\n');
    }
    const total = data.financials?.grandTotal != null ? `\n• Toplam Tutar: ₺${Number(data.financials.grandTotal).toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ${data.financials.currency || 'TRY'}` : '';
    const vat = data.financials?.vatTotal != null ? ` (KDV: ₺${Number(data.financials.vatTotal).toLocaleString('tr-TR', { minimumFractionDigits: 2 })})` : '';
    const itemsCount = data.items?.length ? `\n• Kalem Sayısı: ${data.items.length} adet` : '';
    const tokenInfo = data.tokenUsage ? `\n• Token: 📥 ${data.tokenUsage.tokensIn} In | 📤 ${data.tokenUsage.tokensOut} Out` : '';

    return `📋 **Belge Analiz Özeti (${docType})**\n${summary}${docNo}${date}${sender}${receiver}${naceInfo}${taxDeclInfo}${total}${vat}${itemsCount}${tokenInfo}`;
  }

  private generateAssistantReplyWithAttachments(
    prompt: string,
    attachments?: Array<{ fileName: string; fileSizeFormatted?: string; fileType?: string }>
  ): string {
    if (attachments && attachments.length > 0) {
      const docNames = attachments.map((a) => a.fileName).join(', ');
      return `📁 ${attachments.length} adet belge (${docNames}) başarıyla sisteme yüklendi ve arşivlendi. Belgeler VKN: ${this.currentVkn()} kayıtlarınız ile eşleştirildi.${prompt ? `\n\n"${prompt}" talebiniz işleme alındı.` : ''}`;
    }
    return this.generateAssistantReply(prompt);
  }

  private generateAssistantReply(prompt: string): string {
    const lower = prompt.toLowerCase();
    if (lower.includes('müşteri') || lower.includes('musteri')) {
      return `Kayıtlı ${this.customers().length} müşteriniz bulunmaktadır. Güncel toplam bakiye: ₺${this.customers().reduce((acc, c) => acc + c.balance, 0).toLocaleString('tr-TR')}`;
    }
    if (lower.includes('ürün') || lower.includes('stok') || lower.includes('urun')) {
      return `Katalogda ${this.products().length} çeşit ürün var. Kritik stok seviyesindeki ürünleri "Ürünler" sekmesinden inceleyebilirsiniz.`;
    }
    if (lower.includes('evrak') || lower.includes('fatura')) {
      return `Bekleyen ${this.invoices().filter((i) => i.status === 'pending').length} adet tahsilat faturanız var. Toplam tutar: ₺${this.invoices().reduce((acc, i) => acc + i.totalAmount, 0).toLocaleString('tr-TR')}`;
    }
    if (lower.includes('hesap') || lower.includes('plan')) {
      return `CouchDB üzerinde VKN: ${this.currentVkn()} ile senkronize ${this.accounts().length} adet hesap planı kaydı bulunmaktadır.`;
    }
    if (lower.includes('dolar') || lower.includes('kur') || lower.includes('euro') || lower.includes('rapor')) {
      return `Piyasa verileri CouchDB 'daily_currency_rates' veritabanından güncellenmektedir. Rapor sekmesinden anlık kurları görebilirsiniz.`;
    }
    return `Anlaşıldı. VKN: ${this.currentVkn()} kapsamındaki muhasebe ve kayıt sistemiyle senkronize ettim. Başka nasıl yardımcı olabilirim?`;
  }

  private async processVergiLevhasiIfApplicable(
    parsed: any,
    attachments?: Array<{ fileName: string; fileSizeFormatted?: string; fileType?: string; file?: File; dataUrl?: string }>
  ): Promise<void> {
    try {
      const summary = (parsed.analysis?.summary || '').toLowerCase();
      const docType = (parsed.documentType || '').toLowerCase();
      const isVergiLevhasi = docType.includes('vergi') || docType.includes('levha') || summary.includes('vergi levhası') || summary.includes('vergi levhasi');
      if (!isVergiLevhasi) return;

      const currentVkn = this.currentVkn();
      const docVkn = parsed.parties?.receiver?.vknTckn || parsed.parties?.sender?.vknTckn || '';
      const docUnvan = parsed.parties?.receiver?.name || parsed.parties?.sender?.name || '';
      const docTaxOffice = parsed.parties?.receiver?.taxOffice || parsed.parties?.sender?.taxOffice || '';
      const docAddress = parsed.parties?.receiver?.address || parsed.parties?.sender?.address || '';
      const rawNace = parsed.parties?.receiver?.naceCodes || parsed.parties?.sender?.naceCodes || [];
      const naceCodes = Array.isArray(rawNace) ? rawNace : (rawNace ? [rawNace] : []);
      const taxDeclarations = parsed.taxDeclarations || [];

      let userRole = 'MÜKELLEF';
      try {
        const stored = localStorage.getItem('currentUser');
        if (stored) {
          const u = JSON.parse(stored);
          userRole = (u.userRole || u.type || u.userType || '').toUpperCase();
        }
      } catch {}

      console.log(`[Ustat][VergiLevhasi] Tespit edildi. Rol: ${userRole}, Mükellef VKN: ${docVkn}, Ünvan: ${docUnvan}, NACE: ${naceCodes.length}, Beyan: ${taxDeclarations.length}`);

      // 1. Önce GİB'den VKN ile firma sorgulaması yap
      let gibData: any = null;
      if (docVkn) {
        try {
          console.log(`[Ustat][GIB] VKN: ${docVkn} için GİB sorgulaması başlatılıyor...`);
          const gibRes = await fetch(`${environment.apiBaseUrl}/api/gib/query-taxpayer`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ vknTckn: docVkn }),
          });
          if (gibRes.ok) {
            const gibJson = await gibRes.json();
            gibData = gibJson.rawGibData || gibJson.taxpayer || gibJson;
            console.log('[Ustat][GIB] GİB şirket verisi alındı:', gibData);
          }
        } catch (gibErr) {
          console.warn('[Ustat][GIB] GİB sorgusu hatası / offline:', gibErr);
        }
      }

      // 2. MÜKELLEF veya SMMM Mükellef Cari Ekleme -> PouchDB'ye account kaydı aç: [currentVkn]:account:[docVkn]
      // DİKKAT: SMMM ana düzeydeyken (currentVkn === rootVkn) bu bir CARİ değil, YENİ MÜKELLEF ONBOARDING işlemidir!
      // Bu nedenle SMMM ana ekrandayken PouchDB'ye account cari kaydı AÇILMAZ!
      const isSmmmAtRoot = this.isSmmmUser() && (!this.rootVkn() || currentVkn === this.rootVkn());
      if (!isSmmmAtRoot && docVkn && docVkn !== currentVkn) {
        const accountId = `${currentVkn}:account:${docVkn}`;
        const existing = this.customers().find(c => c._id === accountId || c.vknTckn === docVkn);
        if (!existing) {
          const now = new Date();
          const pad = (n: number) => n.toString().padStart(2, '0');
          const createdAt = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())} ${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}`;

          const resolvedName = (gibData?.unvan && gibData.unvan !== 'MÜKELLEF') ? gibData.unvan : docUnvan;
          const resolvedTaxOffice = (gibData?.taxOffice && gibData.taxOffice !== 'VERGİ DAİRESİ') ? gibData.taxOffice : docTaxOffice;
          const resolvedAddress = (gibData?.address && gibData.address !== 'İSTANBUL, TÜRKİYE') ? gibData.address : docAddress;

          const newAccountDoc: CustomerItem = {
            _id: accountId,
            type: 'account',
            account: '120',
            accountCode: '120',
            subType: 'customer',
            vkn: currentVkn,
            vknTckn: docVkn,
            name: resolvedName,
            company: resolvedName,
            taxOffice: resolvedTaxOffice,
            address: resolvedAddress,
            naceCodes: naceCodes,
            taxDeclarations: taxDeclarations,
            gibData: gibData,
            phone: gibData?.phone || '',
            email: gibData?.email || '',
            city: gibData?.addressInfo?.cityName || resolvedTaxOffice || '',
            balance: 0.0,
            status: 'active',
            sourceDoc: 'Vergi Levhası',
            createdAt,
          };

          try {
            await this.muhasebeDb.put(newAccountDoc);
            this.customers.update(prev => [newAccountDoc, ...prev.filter(c => c._id !== accountId && c.vknTckn !== docVkn)]);
            console.log(`[Ustat][Account] Yeni Hesap Cari Kartı PouchDB'ye kaydedildi (ID: ${accountId})`);
          } catch (e) {
            console.warn('[Ustat][Account] PouchDB put notice:', e);
          }
        }
      }
    } catch (err) {
      console.warn('[Ustat][VergiLevhasi] Auto process notice:', err);
    }

    // Refresh SMMM portfolio of taxpayers
    const root = this.rootVkn() || this.currentVkn();
    if (root) {
      this.fetchMukellefler(root).catch(() => {});
    }
  }

  /**
   * Automatically processes expense receipts (Fiş / Masraf Makbuzu / POS fişi)
   * into compliant TDHP Yevmiye transactions, saving them to PouchDB & CouchDB
   * and replicating to the Fişler tab and Google Drive.
   */
  async processFisIfApplicable(parsed: any, attachments?: any[]): Promise<void> {
    try {
      const vkn = this.currentVkn();
      if (!vkn || !/^\d{10,11}$/.test(vkn)) {
        console.warn('[Ustat][ProcessFis] Invalid or missing VKN, skipping yevmiye creation.');
        return;
      }

      const docType = (parsed.documentType || 'Mali Belge').toUpperCase();
      console.log('[Ustat][ProcessFis] 🧾 BELGE YEVMİYELEŞTİRME BAŞLATILIYOR (Kısıtlama Yok) for VKN:', vkn, 'DocType:', docType);

      const docDate = parsed.documentDate || parsed.financials?.date || parsed.date || new Date().toISOString().split('T')[0];
      const cleanDate = docDate.replace(/-/g, '');
      const docNo = (parsed.financials?.documentNo || parsed.receiptNumber || parsed.documentNumber || String(Date.now()).slice(-5)).toString().padStart(5, '0');
      const merchantTitle = parsed.parties?.sender?.name || parsed.merchant?.name || 'Genel Muhasebe';
      const merchantTaxId = parsed.parties?.sender?.vknTckn || parsed.merchant?.taxId || '';

      const yevDocs: YevmiyeTransactionItem[] = [];

      // Case 0: Multi-voucher journal entries (all vouchers and all lines preserved dynamically)
      const jEntries = Array.isArray(parsed.journal_entries) ? parsed.journal_entries : (Array.isArray(parsed.vouchers) ? parsed.vouchers : null);
      if (jEntries && jEntries.length > 0) {
        for (const v of jEntries) {
          const vNo = (v.voucherNumber || v.document_number || v.docNo || docNo).toString().padStart(5, '0');
          const vDate = v.date || v.document_date || docDate;
          const vCleanDate = vDate.replace(/-/g, '');
          const vType = v.documentType || v.document_type || docType || 'Virman';
          const vDesc = v.description || merchantTitle;
          const lines = Array.isArray(v.entries) ? v.entries : (Array.isArray(v.lines) ? v.lines : []);
          let seq = 1;
          for (const line of lines) {
            const accCode = String(line.accountCode || line.account_code || line.code || '770.01').trim();
            const accName = line.accountName || line.account_name || line.name || 'Cari / Muhasebe Hesabı';
            const debit = Math.abs(Number(line.debit || 0));
            const credit = Math.abs(Number(line.credit || 0));
            const isDebit = debit > 0 || (line.type ? line.type.toUpperCase() === 'BORC' : false);
            const lineDesc = line.description || vDesc;
            const docId = `${vkn}:yev:${vCleanDate}:${vNo}:${seq}`;
            yevDocs.push({
              _id: docId,
              entityVkn: vkn,
              voucherNumber: vNo,
              sequenceNumber: seq,
              orderNumber: seq,
              date: vDate,
              accountCode: accCode,
              accountLevel1: accCode.split('.')[0] || accCode.substring(0, 3),
              accountName: accName,
              classification: isDebit ? (accCode.startsWith('7') ? 'GİDER' : (accCode.startsWith('65') ? 'GİDER' : 'AKTİF')) : (accCode.startsWith('64') ? 'GELİR' : 'PASİF'),
              description: lineDesc,
              debit: isDebit ? (debit || Number(line.amount || 0)) : 0,
              credit: !isDebit ? (credit || Number(line.amount || 0)) : 0,
              entryDate: vDate,
              documentDate: vDate,
              documentNumber: vNo,
              documentType: vType,
              counterpartTitle: merchantTitle,
              counterpartTaxId: merchantTaxId,
              paymentMethod: 'Mahsup',
              type: 'yevmiye',
              vkn,
            });
            seq++;
          }
        }
      } else if (Array.isArray(parsed.suggestedAccountingAccounts) && parsed.suggestedAccountingAccounts.length > 0) {
        let seq = 1;
        for (const acc of parsed.suggestedAccountingAccounts) {
          const accCode = acc.accountCode || (acc.type === 'BORC' ? '770.01' : '108.01');
          const accName = acc.accountName || (accCode.startsWith('7') ? 'Genel Yönetim Giderleri' : (accCode.startsWith('191') ? 'İndirilecek KDV' : (accCode.startsWith('6') ? 'Kambiyo/Gelir-Gider' : 'Kasa / POS')));
          const isDebit = (acc.type || '').toUpperCase() === 'BORC';
          let amount = Math.abs(Number(acc.amount || 0));

          // If amount is 0, check if items or financialSummary have amount
          if (amount === 0 && Array.isArray(parsed.items) && parsed.items.length >= seq) {
            const it = parsed.items[seq - 1];
            amount = Math.abs(Number(it.totalAmount || it.unitPrice || 0));
          }
          if (amount === 0 && parsed.financialSummary?.totalGrossAmount) {
            amount = Math.abs(Number(parsed.financialSummary.totalGrossAmount));
          }

          const docId = `${vkn}:yev:${cleanDate}:${docNo}:${seq}`;
          const yevItem: YevmiyeTransactionItem = {
            _id: docId,
            entityVkn: vkn,
            voucherNumber: docNo,
            sequenceNumber: seq,
            orderNumber: seq,
            date: docDate,
            accountCode: accCode,
            accountLevel1: accCode.split('.')[0] || accCode.substring(0, 3),
            accountName: accName,
            classification: isDebit ? (accCode.startsWith('7') ? 'GİDER' : (accCode.startsWith('65') ? 'GİDER' : 'AKTİF')) : (accCode.startsWith('64') ? 'GELİR' : 'PASİF'),
            description: `${merchantTitle} - ${accName}`.trim(),
            debit: isDebit ? amount : 0,
            credit: !isDebit ? amount : 0,
            entryDate: docDate,
            documentDate: docDate,
            documentNumber: docNo,
            documentType: docType || 'Yevmiye',
            counterpartTitle: merchantTitle,
            counterpartTaxId: merchantTaxId,
            paymentMethod: accCode.startsWith('108') ? 'Kredi Kartı' : (accCode.startsWith('100') ? 'Nakit' : 'Mahsup'),
            type: 'yevmiye',
            vkn,
          };
          yevDocs.push(yevItem);
          seq++;
        }
      } else if (Array.isArray(parsed.items) && parsed.items.length > 0 && parsed.items.some((it: any) => it.code || it.accountCode)) {
        let seq = 1;
        for (const it of parsed.items) {
          const accCode = it.accountCode || it.code || '770.01';
          const accName = it.accountName || it.name || 'Genel Muhasebe Kaydı';
          const amount = Math.abs(Number(it.totalAmount || it.unitPrice || it.amount || 0));
          const isDebit = it.type ? (it.type.toUpperCase() === 'BORC') : (!accCode.startsWith('64') && !accCode.startsWith('3') && !accCode.startsWith('5'));

          const docId = `${vkn}:yev:${cleanDate}:${docNo}:${seq}`;
          yevDocs.push({
            _id: docId,
            entityVkn: vkn,
            voucherNumber: docNo,
            sequenceNumber: seq,
            orderNumber: seq,
            date: docDate,
            accountCode: accCode,
            accountLevel1: accCode.split('.')[0] || accCode.substring(0, 3),
            accountName: accName,
            classification: isDebit ? 'GİDER' : 'GELİR',
            description: `${merchantTitle} - ${accName}`.trim(),
            debit: isDebit ? amount : 0,
            credit: !isDebit ? amount : 0,
            entryDate: docDate,
            documentDate: docDate,
            documentNumber: docNo,
            documentType: docType || 'Yevmiye',
            counterpartTitle: merchantTitle,
            counterpartTaxId: merchantTaxId,
            paymentMethod: 'Mahsup',
            type: 'yevmiye',
            vkn,
          });
          seq++;
        }
      } else {
        // Case 2: Derive TDHP entries from financial summary and tax breakdown
        const gross = Math.abs(Number(parsed.financialSummary?.totalGrossAmount || parsed.totalAmount || 0));
        const tax = Math.abs(Number(parsed.financialSummary?.totalTaxAmount || parsed.taxAmount || 0));
        const net = Math.abs(Number(parsed.financialSummary?.totalNetAmount || (gross - tax) || gross));

        if (gross > 0) {
          let seq = 1;
          // Gider Kalemi (770 Genel Yönetim Giderleri - BORÇ)
          if (net > 0) {
            yevDocs.push({
              _id: `${vkn}:yev:${cleanDate}:${docNo}:${seq}`,
              entityVkn: vkn,
              voucherNumber: docNo,
              sequenceNumber: seq,
              orderNumber: seq,
              date: docDate,
              accountCode: '770.05',
              accountLevel1: '770',
              accountName: 'Genel Yönetim Giderleri',
              classification: 'GİDER',
              description: `${merchantTitle} Harcama Tutarı`,
              debit: net,
              credit: 0,
              entryDate: docDate,
              documentDate: docDate,
              documentNumber: docNo,
              documentType: 'Fiş',
              counterpartTitle: merchantTitle,
              counterpartTaxId: merchantTaxId,
              type: 'yevmiye',
              vkn,
            });
            seq++;
          }

          // KDV Kalemi (191 İndirilecek KDV - BORÇ)
          if (tax > 0) {
            yevDocs.push({
              _id: `${vkn}:yev:${cleanDate}:${docNo}:${seq}`,
              entityVkn: vkn,
              voucherNumber: docNo,
              sequenceNumber: seq,
              orderNumber: seq,
              date: docDate,
              accountCode: '191.01',
              accountLevel1: '191',
              accountName: 'İndirilecek KDV',
              classification: 'AKTİF',
              description: `${merchantTitle} KDV Tutarı`,
              debit: tax,
              credit: 0,
              entryDate: docDate,
              documentDate: docDate,
              documentNumber: docNo,
              documentType: 'Fiş',
              counterpartTitle: merchantTitle,
              counterpartTaxId: merchantTaxId,
              type: 'yevmiye',
              vkn,
            });
            seq++;
          }

          // Ödeme Kalemi (108 POS / Kredi Kartı veya 100 Kasa - ALACAK)
          const isCard = (parsed.payment?.method || '').toUpperCase() === 'CARD' || Boolean(parsed.payment?.cardType);
          yevDocs.push({
            _id: `${vkn}:yev:${cleanDate}:${docNo}:${seq}`,
            entityVkn: vkn,
            voucherNumber: docNo,
            sequenceNumber: seq,
            orderNumber: seq,
            date: docDate,
            accountCode: isCard ? '108.01' : '100.01',
            accountLevel1: isCard ? '108' : '100',
            accountName: isCard ? 'Kredi Kartı Slipleri' : 'Kasa TL',
            classification: 'AKTİF',
            description: `${merchantTitle} ${isCard ? 'Kredi Kartı Ödemesi' : 'Nakit Ödeme'}`,
            debit: 0,
            credit: gross,
            entryDate: docDate,
            documentDate: docDate,
            documentNumber: docNo,
            documentType: 'Fiş',
            counterpartTitle: merchantTitle,
            counterpartTaxId: merchantTaxId,
            paymentMethod: isCard ? 'Kredi Kartı' : 'Nakit',
            type: 'yevmiye',
            vkn,
          });
        }
      }

      // Save all generated yevmiye docs into PouchDB & update in-memory signal
      if (yevDocs.length > 0) {
        for (const yDoc of yevDocs) {
          try {
            await this.muhasebeDb.put(yDoc);
            console.log(`[Ustat][ProcessFis] ✅ Yevmiye kaydı PouchDB'ye kaydedildi: ${yDoc._id} (${yDoc.accountCode} - ${yDoc.debit > 0 ? 'Borç: ' + yDoc.debit : 'Alacak: ' + yDoc.credit})`);
          } catch (putErr) {
            console.warn('[Ustat][ProcessFis] Yevmiye put notice:', putErr);
          }
        }

        this.yevmiyeTransactions.update((prev) => {
          const newIds = new Set(yevDocs.map((d) => d._id));
          return [...yevDocs, ...prev.filter((p) => !newIds.has(p._id))];
        });

        // Update Google Drive yevmiye spreadsheet
        try {
          await this.googleWorkspace.saveYevmiyeToGoogleSheet(vkn, this.yevmiyeTransactions());
        } catch (driveErr) {
          console.warn('[Ustat][ProcessFis] Google Drive sync deferred:', driveErr);
        }
      }
    } catch (err) {
      console.error('[Ustat][ProcessFis] Error processing fiş to yevmiye:', err);
    }
  }

  /**
   * Fast prefix range query for all customer/seller account records of a mükellef.
   * Utilizes PouchDB B-Tree indexing without secondary map-reduce overhead.
   */
  async getCustomerAccounts(vkn: string = this.currentVkn()): Promise<CustomerItem[]> {
    try {
      const res = await this.muhasebeDb.allDocs({
        startkey: `${vkn}:account:`,
        endkey: `${vkn}:account:\ufff0`,
        include_docs: true,
      });
      return res.rows.map((r) => r.doc as CustomerItem).filter(Boolean);
    } catch (e) {
      console.warn('[DatabaseService] getCustomerAccounts error:', e);
      return [];
    }
  }

  async addCustomer(customer: Omit<CustomerItem, 'type' | 'vkn'>): Promise<void> {
    console.log('[Ustat][DatabaseService] Adding new customer:', customer);
    const now = new Date();
    const pad = (n: number) => n.toString().padStart(2, '0');
    const createdAt = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())} ${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}`;
    await this.addDocument('muhasebe', { ...customer, createdAt: customer.createdAt || createdAt, type: 'customer', vkn: this.currentVkn() });
  }

  async addProduct(product: Omit<ProductItem, 'type' | 'vkn'>): Promise<void> {
    console.log('[Ustat][DatabaseService] Adding new product:', product);
    const now = new Date();
    const pad = (n: number) => n.toString().padStart(2, '0');
    const createdAt = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())} ${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}`;
    await this.addDocument('muhasebe', { ...product, createdAt: product.createdAt || createdAt, type: 'product', vkn: this.currentVkn() });
  }

  async addInvoice(invoice: Omit<InvoiceItem, 'type' | 'vkn'>): Promise<void> {
    console.log('[Ustat][DatabaseService] Adding new invoice:', invoice);
    const now = new Date();
    const pad = (n: number) => n.toString().padStart(2, '0');
    const createdAt = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())} ${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}`;
    await this.addDocument('muhasebe', { ...invoice, createdAt: invoice.createdAt || createdAt, type: 'invoice', vkn: this.currentVkn() });
  }

  // --- Initial Demo Seeding with [vkn]: Prefixes ---

  private async seedCurrencyRates(): Promise<void> {
    this.currencyRates.set([]);
  }

  private async seedMuhasebeData(): Promise<void> {
    this.customers.set([]);
    this.products.set([]);
    this.invoices.set([]);
    this.chatMessages.set([]);
  }

  validateFilenameForCompany(
    fileName: string,
    expectedVkn: string,
    expectedTitle: string,
    knownMukellefler: any[] = []
  ): { isValid: boolean; code?: string; detectedCompany?: string; reason?: string } {
    return { isValid: true };
  }

  async addOriginalDocument(file: File): Promise<UploadedDocumentItem> {
    const vkn = this.currentVkn();

    const ext = (file.name.split('.').pop() || '').toLowerCase();
    let docType: UploadedDocumentItem['documentType'] = 'DİĞER';
    if (ext === 'pdf') docType = 'PDF';
    else if (ext === 'xlsx') docType = 'XLSX';
    else if (ext === 'xls') docType = 'EXCEL';
    else if (ext === 'csv') docType = 'CSV';
    else if (ext === 'doc' || ext === 'docx') docType = 'DOC';
    else if (ext === 'xml') docType = 'XML';
    else if (ext === 'json') docType = 'JSON';
    else if (['jpg', 'jpeg', 'png', 'webp', 'gif', 'bmp', 'svg'].includes(ext) || file.type.startsWith('image/')) docType = 'IMAGE';

    const size = file.size;
    const sizeFormatted = size < 1024 * 1024
      ? `${(size / 1024).toFixed(1)} KB`
      : `${(size / (1024 * 1024)).toFixed(2)} MB`;

    const now = new Date();
    const pad = (n: number) => n.toString().padStart(2, '0');
    const uploadDateTime = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())} ${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}`;

    const newDoc: UploadedDocumentItem = {
      _id: `${vkn}:doc:${Date.now()}`,
      vkn,
      type: 'original_doc',
      fileName: file.name,
      fileSize: size,
      fileSizeFormatted: sizeFormatted,
      documentType: docType,
      mimeType: file.type || 'application/octet-stream',
      uploadDateTime,
      status: 'uploaded',
    };

    try {
      await this.muhasebeDb.put(newDoc);
    } catch (e) {
      console.warn('[DatabaseService] Error saving doc to PouchDB:', e);
    }

    this.originalDocs.update((docs) => [newDoc, ...docs]);

    // Asynchronously sync original file to server companies directory
    try {
      const isValidVkn = /^\d{10,11}$/.test(vkn);
      if (!isValidVkn) {
        return newDoc;
      }

      const formData = new FormData();
      formData.append('file', file, file.name);

      const rootVkn = this.rootVkn() || vkn;
      const isSmmm = this.isSmmmUser();
      const isSelf = (vkn === rootVkn);

      if (isSmmm) {
        formData.append('smmmVkn', rootVkn);
        if (vkn && vkn !== rootVkn) {
          // SMMM bir mükellef seçmiş ve o mükellef için hesap/cari ekliyor
          formData.append('mukellefVkn', vkn);
          formData.append('mode', 'add_account_to_mukellef');
        } else {
          // SMMM ana düzeyde: Yeni Mükellef Başlatma (Onboarding)
          formData.append('mode', 'onboarding');
        }
      }

      fetch(`${environment.apiBaseUrl}/api/companies/${vkn}/upload`, {
        method: 'POST',
        body: formData,
      }).then(async (res) => {
        if (res.ok) {
          const sJson = await res.json();
          console.log('[Ustat][Upload] Server company sync completed:', sJson);
        }
      }).catch((syncErr) => {
        console.warn('[Ustat][Upload] Server sync notice:', syncErr);
      });

      // Asynchronously backup to Google Drive under 'hesaplar/[vkn]/docs/[vkn]_yevmiye.ext'
      const extLower = (file.name.split('.').pop() || 'xlsx').toLowerCase();
      const driveDocName = `${vkn}_yevmiye.${extLower}`;
      this.googleWorkspace.uploadFileToDriveDocs(vkn, file, driveDocName).catch((gErr) => {
        console.log('[Ustat][Upload] Google Drive docs backup deferred:', gErr?.message || gErr);
      });
    } catch (err) {
      console.warn('[Ustat][Upload] Server sync skipped:', err);
    }

    return newDoc;
  }

  async deleteOriginalDocument(id: string): Promise<void> {
    try {
      const doc = await this.muhasebeDb.get(id);
      await this.muhasebeDb.remove(doc);
    } catch (e) {
      console.warn('[DatabaseService] Error removing doc from PouchDB:', e);
    }
    this.originalDocs.update((docs) => docs.filter((d) => d._id !== id));
  }

  /**
   * Saves a finalized invoice to PouchDB & in-memory signals.
   */
  async saveCompletedInvoice(inv: {
    invoiceNo: string;
    customerName: string;
    totalAmount: number;
    currency?: string;
    status?: 'paid' | 'pending' | 'draft';
  }): Promise<InvoiceItem> {
    const vkn = this.currentVkn();
    const newInv: InvoiceItem = {
      _id: `${vkn}:invoice:${Date.now()}_${Math.random().toString(36).substring(2, 6)}`,
      vkn,
      type: 'invoice',
      invoiceNo: inv.invoiceNo,
      customerName: inv.customerName,
      date: new Date().toISOString().split('T')[0],
      totalAmount: inv.totalAmount,
      currency: inv.currency || 'TRY',
      status: inv.status || 'pending',
      createdAt: new Date().toISOString(),
    };

    try {
      if (this.muhasebeDb) {
        await this.muhasebeDb.put(newInv);
      }
    } catch (e) {
      console.warn('[DatabaseService] Error saving invoice to PouchDB:', e);
    }

    this.invoices.update((list) => [newInv, ...list]);
    return newInv;
  }

  // --- TOKEN USAGE LEDGER & TRACKING ---

  async recordTokenUsage(
    feature: 'Gemini Live' | 'Sesli Fatura' | 'Asistan Sohbet' | 'Evrak Analiz' | 'Google Sync',
    tokensIn: number,
    tokensOut: number,
    model = 'gemini-2.0-flash'
  ): Promise<TokenUsageItem> {
    const vkn = this.currentVkn();
    const now = new Date();
    const pad = (n: number) => n.toString().padStart(2, '0');
    const date = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
    const dateTime = `${date} ${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}`;

    const item: TokenUsageItem = {
      _id: `${vkn}:token:${Date.now()}_${Math.random().toString(36).substring(2, 6)}`,
      vkn,
      type: 'token_usage',
      date,
      dateTime,
      feature,
      model,
      tokensIn,
      tokensOut,
      totalTokens: tokensIn + tokensOut,
    };

    try {
      await this.muhasebeDb.put(item);
    } catch (e) {
      console.warn('[DatabaseService] Error saving token usage:', e);
    }

    this.tokenUsages.update((list) => [item, ...list]);
    return item;
  }

  getInitialTokenUsages(vkn: string): TokenUsageItem[] {
    return [];
  }
}
